import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../api/axiosInstance';
import {
  PhoneIcon,
  PlusIcon,
  ArrowPathIcon,
  MagnifyingGlassIcon,
  UserIcon,
  CpuChipIcon,
  LinkIcon,
} from '@heroicons/react/24/outline';
import logger from '../../../utils/logger';
import {
  PageHeader,
  StatCard,
  StatGrid,
  EmptyState,
  ConfirmDialog,
  LoadingBlock,
  TextInput,
  BORDER,
  TEXT_MUTED,
  btn,
} from './components';
import NumberCard from './dids/NumberCard';
import BuyNumbersTab from './dids/BuyNumbersTab';
import AssignModal from './dids/AssignModal';
import RoutingModal from './dids/RoutingModal';
import useNumberSelection from './dids/useNumberSelection';
import { asArray } from './components/format';

/**
 * Phone number management.
 *
 * Was a single 1,729-line file containing five screens: a numbers list, a
 * search-and-buy table, and three modals — plus a 37-line country table and
 * an 8-function selection state machine inline in the render body. Split into
 * dids/ and rebuilt on the shared Voice kit.
 *
 * Bugs fixed in the process:
 *  - The State and City search inputs were bound to state but never appended
 *    to the query, so typing in them did nothing. State is now sent (and only
 *    shown for countries where it applies); City is dropped — the search API
 *    doesn't accept it.
 *  - selectedTrunkForOrder was declared and never read or written, a vestige
 *    of a removed "pick a trunk before ordering" flow. The modal it belonged
 *    to is now a plain confirmation.
 *  - The `trunks` fetch existed only to disable a button; it's gone with the
 *    flow that needed it.
 *  - `Table` was imported but never rendered.
 */

const DIDManagement = ({ showNotification }) => {
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('numbers');
  const [loading, setLoading] = useState(true);
  const [dids, setDids] = useState([]);
  const [query, setQuery] = useState('');

  // Buy tab
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [purchasing, setPurchasing] = useState(null);
  const [bulkPurchasing, setBulkPurchasing] = useState(false);
  const [searchFilters, setSearchFilters] = useState({
    country: 'US',
    areaCode: '',
    contains: '',
    numberType: 'local',
    state: '',
    limit: 25,
  });

  // Assignment
  const [users, setUsers] = useState([]);
  const [agents, setAgents] = useState([]);
  const [assigningDID, setAssigningDID] = useState(null);
  const [savingAssignment, setSavingAssignment] = useState(false);

  // Routing
  const [routingDID, setRoutingDID] = useState(null);
  const [routingForm, setRoutingForm] = useState(null);
  const [credentials, setCredentials] = useState([]);
  const [savingRouting, setSavingRouting] = useState(false);

  const [confirm, setConfirm] = useState(null);
  const [confirmBusy, setConfirmBusy] = useState(false);

  const selection = useNumberSelection(searchResults);

  // ── Data ────────────────────────────────────────────────────────────────

  const fetchDIDs = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/api/sip/dids');
      if (res.data.success) setDids(asArray(res.data, 'dids'));
    } catch (error) {
      logger.error('Error fetching DIDs:', error);
      showNotification?.('Failed to load phone numbers', 'error');
    } finally {
      setLoading(false);
    }
  }, [showNotification]);

  useEffect(() => {
    // Sync from the carrier first so numbers bought elsewhere show up, then
    // load. Fire-and-forget: a sync failure shouldn't block the list.
    axiosInstance.post('/api/sip/dids/sync', {}, { timeout: 60000 }).catch(() => {});
    fetchDIDs();

    axiosInstance
      .get('/api/business/users?limit=100&status=active')
      .then((r) => setUsers(asArray(r.data, 'users')))
      .catch(() => {});
    axiosInstance
      .get('/api/ai/agents')
      .then((r) => setAgents(asArray(r.data, 'agents')))
      .catch(() => {});
  }, [fetchDIDs]);

  // ── Search & buy ────────────────────────────────────────────────────────

  const handleSearch = async () => {
    try {
      setSearching(true);
      const params = new URLSearchParams();
      if (searchFilters.country) params.append('country', searchFilters.country);
      if (searchFilters.areaCode) params.append('areaCode', searchFilters.areaCode);
      if (searchFilters.contains) params.append('contains', searchFilters.contains);
      if (searchFilters.numberType) params.append('numberType', searchFilters.numberType);
      // Previously collected but never sent — the input was inert.
      if (searchFilters.state) params.append('state', searchFilters.state);
      params.append('limit', searchFilters.limit);

      const res = await axiosInstance.get(`/api/sip/dids/search?${params.toString()}`);
      if (res.data.success) {
        const found = asArray(res.data, 'numbers');
        setSearchResults(found);
        selection.clear();
        if (found.length === 0) {
          showNotification?.('No numbers found matching your criteria', 'info');
        }
      }
    } catch (error) {
      logger.error('Error searching numbers:', error);
      showNotification?.('Failed to search phone numbers', 'error');
    } finally {
      setSearching(false);
    }
  };

  /** Area code from a possibly-masked number, for inexplicit ordering. */
  const areaCodeOf = (phoneNumber) => {
    const cleaned = String(phoneNumber).replace(/\D/g, '');
    const withoutCountry = cleaned.startsWith('1') ? cleaned.slice(1) : cleaned;
    return withoutCountry.slice(0, 3);
  };

  /**
   * Shared failure handling for both order paths.
   *
   * The server charges the card BEFORE provisioning, so a 402 has to be told
   * apart: PAYMENT_METHOD_REQUIRED means send them to billing, anything else
   * means the card was declined and nothing was provisioned or charged.
   */
  const handleOrderError = (error, fallbackMessage) => {
    logger.error(fallbackMessage, error);

    if (!error.response) {
      // A timeout doesn't mean the order failed — refresh and let the list
      // show the truth.
      showNotification?.('Request timed out. Refreshing to check status…', 'warning');
      fetchDIDs();
      setActiveTab('numbers');
      return;
    }

    if (error.response.status === 402) {
      const code = error.response.data?.code;
      if (code === 'PAYMENT_METHOD_REQUIRED' || error.response.data?.requiresPaymentMethod) {
        showNotification?.('Add a payment method to buy a phone number. Redirecting to billing…', 'warning');
        setTimeout(() => navigate('/billing'), 1200);
      } else {
        showNotification?.(
          error.response.data?.error ||
            'Your card was declined. Update your payment method and try again.',
          'error'
        );
      }
      return;
    }

    showNotification?.(error.response.data?.error || fallbackMessage, 'error');
  };

  const handlePurchase = async (number) => {
    try {
      setPurchasing(number.phoneNumber);
      const res = await axiosInstance.post(
        '/api/sip/dids/order',
        {
          phoneNumber: number.phoneNumber,
          friendlyName:
            `${number.locality || ''} ${number.region || ''}`.trim() || number.phoneNumber,
          searchCriteria: {
            areaCode: areaCodeOf(number.phoneNumber) || searchFilters.areaCode,
            country: searchFilters.country || 'US',
            numberType: searchFilters.numberType || 'local',
          },
        },
        { timeout: 60000 }
      );

      if (res.data.success) {
        showNotification?.('Phone number purchased', 'success');
        fetchDIDs();
        setActiveTab('numbers');
        setSearchResults([]);
      }
    } catch (error) {
      handleOrderError(error, 'Failed to purchase phone number');
    } finally {
      setPurchasing(null);
    }
  };

  const handleBulkPurchase = async () => {
    if (selection.count === 0) return;
    try {
      setBulkPurchasing(true);
      const res = await axiosInstance.post(
        '/api/sip/dids/bulk-order',
        {
          phoneNumbers: selection.selected.map((n) => n.phoneNumber),
          searchCriteria: {
            areaCode: areaCodeOf(selection.selected[0].phoneNumber) || searchFilters.areaCode,
            country: searchFilters.country || 'US',
            numberType: searchFilters.numberType || 'local',
            quantity: selection.count,
          },
        },
        { timeout: 120000 }
      );

      if (res.data.success) {
        const { totalOrdered, totalFailed } = res.data.data || {};
        showNotification?.(
          `Ordered ${totalOrdered} number${totalOrdered === 1 ? '' : 's'}${
            totalFailed > 0 ? `, ${totalFailed} failed` : ''
          }`,
          totalFailed > 0 ? 'warning' : 'success'
        );
        selection.clear();
        setSearchResults([]);
        fetchDIDs();
        setActiveTab('numbers');
      }
    } catch (error) {
      handleOrderError(error, 'Failed to buy the selected numbers');
    } finally {
      setBulkPurchasing(false);
      setConfirm(null);
    }
  };

  // ── Assignment ──────────────────────────────────────────────────────────

  const handleAssign = async ({ type, target, extension }) => {
    if (!target) return;
    const id = assigningDID._id;
    const routes = {
      user: [`/api/dids/${id}/assign/user`, { userId: target, extension: extension || undefined }],
      agent: [`/api/dids/${id}/assign/agent`, { agentId: target }],
      department: [`/api/dids/${id}/assign/department`, { department: target }],
    };
    const [endpoint, payload] = routes[type] || [];
    if (!endpoint) return;

    try {
      setSavingAssignment(true);
      const res = await axiosInstance.post(endpoint, payload);
      if (res.data.success) {
        showNotification?.(res.data.message || 'Number assigned', 'success');
        setAssigningDID(null);
        fetchDIDs();
      }
    } catch (error) {
      logger.error('Error assigning DID:', error);
      showNotification?.(
        error.response?.data?.message || error.response?.data?.error || 'Failed to assign number',
        'error'
      );
    } finally {
      setSavingAssignment(false);
    }
  };

  const runConfirmed = async (fn, success, failure) => {
    setConfirmBusy(true);
    try {
      const res = await fn();
      if (res.data.success) {
        showNotification?.(success, 'success');
        fetchDIDs();
      }
    } catch (error) {
      logger.error(failure, error);
      showNotification?.(error.response?.data?.error || failure, 'error');
    } finally {
      setConfirmBusy(false);
      setConfirm(null);
    }
  };

  // ── Routing ─────────────────────────────────────────────────────────────

  const openRouting = async (did) => {
    setRoutingDID(did);
    setRoutingForm({
      type: did.routing?.type || 'sip_credential',
      sipCredentialId: did.routing?.sipCredentialId || '',
      sipUri: did.routing?.sipUri || '',
      forwardTo: did.routing?.forwardTo || '',
      failover: {
        enabled: did.routing?.failover?.enabled || false,
        type: did.routing?.failover?.type || 'voicemail',
        destination: did.routing?.failover?.destination || '',
        timeoutSeconds: did.routing?.failover?.timeoutSeconds || 30,
      },
    });

    const trunkId = did.sipTrunkId?._id || did.sipTrunkId;
    if (trunkId) {
      try {
        const res = await axiosInstance.get(`/api/sip/trunks/${trunkId}/credentials`);
        setCredentials(asArray(res.data, 'credentials'));
      } catch {
        setCredentials([]);
      }
    } else {
      setCredentials([]);
    }
  };

  const saveRouting = async () => {
    try {
      setSavingRouting(true);
      const res = await axiosInstance.put(`/api/sip/dids/${routingDID._id}/routing`, routingForm);
      if (res.data.success) {
        showNotification?.('Routing updated', 'success');
        setRoutingDID(null);
        setRoutingForm(null);
        fetchDIDs();
      }
    } catch (error) {
      logger.error('Error updating routing:', error);
      showNotification?.(error.response?.data?.error || 'Failed to update routing', 'error');
    } finally {
      setSavingRouting(false);
    }
  };

  // ── Derived ─────────────────────────────────────────────────────────────

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return dids;
    const digits = q.replace(/\D/g, '');
    return dids.filter((d) => {
      const number = String(d.phoneNumber || '');
      if (digits && number.replace(/\D/g, '').includes(digits)) return true;
      const a = d.assignment || {};
      const name = `${a.userId?.firstName || ''} ${a.userId?.lastName || ''} ${
        a.agentId?.agentName || ''
      } ${a.department || ''}`.toLowerCase();
      return name.includes(q) || String(d.friendlyName || '').toLowerCase().includes(q);
    });
  }, [dids, query]);

  const stats = useMemo(
    () => ({
      total: dids.length,
      assigned: dids.filter((d) => d.assignment && d.assignment.type !== 'unassigned').length,
      agents: dids.filter((d) => d.assignment?.type === 'agent').length,
      unassigned: dids.filter((d) => !d.assignment || d.assignment.type === 'unassigned').length,
    }),
    [dids]
  );

  const tabs = [
    { id: 'numbers', label: 'My numbers', count: dids.length },
    { id: 'search', label: 'Buy numbers' },
  ];

  return (
    <div className="h-full overflow-y-auto bg-gray-50 dark:bg-[#0d0d0d]">
      <PageHeader
        title="Phone numbers"
        subtitle="Buy, assign and route your DIDs"
        actions={
          <>
            <button onClick={fetchDIDs} className={btn('secondary', 'sm')}>
              <ArrowPathIcon className="w-4 h-4" />
              Refresh
            </button>
            {activeTab === 'numbers' && (
              <button onClick={() => setActiveTab('search')} className={btn('primary', 'sm')}>
                <PlusIcon className="w-4 h-4" />
                Buy number
              </button>
            )}
          </>
        }
      />

      <div className="max-w-7xl mx-auto p-6 space-y-5">
        <StatGrid cols={4}>
          <StatCard label="Total numbers" value={stats.total} icon={PhoneIcon} tone="info" />
          <StatCard label="Assigned" value={stats.assigned} icon={UserIcon} tone="success" />
          <StatCard label="On AI agents" value={stats.agents} icon={CpuChipIcon} tone="accent" />
          <StatCard label="Unassigned" value={stats.unassigned} icon={LinkIcon} tone="neutral" />
        </StatGrid>

        <div className={`flex items-center gap-1 border-b ${BORDER}`}>
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              className={`relative px-3 py-2.5 text-[13px] font-medium transition-colors ${
                activeTab === t.id
                  ? 'text-orange-500'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              {t.label}
              {t.count != null && <span className={`ml-1.5 ${TEXT_MUTED}`}>{t.count}</span>}
              {activeTab === t.id && (
                <span className="absolute bottom-0 inset-x-2 h-0.5 rounded-full bg-orange-500" />
              )}
            </button>
          ))}
        </div>

        {activeTab === 'numbers' ? (
          loading ? (
            <LoadingBlock label="Loading phone numbers…" />
          ) : dids.length === 0 ? (
            <EmptyState
              icon={PhoneIcon}
              title="No phone numbers yet"
              description="Buy a number to start making and receiving calls."
              action={
                <button onClick={() => setActiveTab('search')} className={btn('primary', 'sm')}>
                  <PlusIcon className="w-4 h-4" />
                  Buy your first number
                </button>
              }
            />
          ) : (
            <>
              {/* Search across a growing list — the original had no way to
                  find a number once you owned more than a screenful. */}
              <div className="relative max-w-sm">
                <MagnifyingGlassIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                <TextInput
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search numbers or assignees"
                  className="pl-9"
                />
              </div>

              {filtered.length === 0 ? (
                <EmptyState
                  icon={MagnifyingGlassIcon}
                  title="No matches"
                  description={`Nothing matches "${query}".`}
                  size="sm"
                />
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {filtered.map((did) => (
                    <NumberCard
                      key={did._id}
                      did={did}
                      onAssign={setAssigningDID}
                      onRouting={openRouting}
                      onUnassign={(d) =>
                        setConfirm({
                          title: 'Unassign this number?',
                          message: `${d.phoneNumber} will stop ringing its current destination.`,
                          confirmLabel: 'Unassign',
                          variant: 'danger',
                          action: () =>
                            runConfirmed(
                              () => axiosInstance.post(`/api/dids/${d._id}/unassign`),
                              'Number unassigned',
                              'Failed to unassign the number'
                            ),
                        })
                      }
                      onRelease={(d) =>
                        setConfirm({
                          title: 'Release this number?',
                          message: `${d.phoneNumber} will be returned to the carrier and can't be recovered. Billing stops at the end of the current period.`,
                          confirmLabel: 'Release number',
                          variant: 'danger',
                          action: () =>
                            runConfirmed(
                              () => axiosInstance.delete(`/api/sip/dids/${d._id}`),
                              'Number released',
                              'Failed to release the number'
                            ),
                        })
                      }
                    />
                  ))}
                </div>
              )}
            </>
          )
        ) : (
          <BuyNumbersTab
            filters={searchFilters}
            setFilters={setSearchFilters}
            onSearch={handleSearch}
            searching={searching}
            results={searchResults}
            selection={selection}
            purchasingNumber={purchasing}
            onBuyOne={handlePurchase}
            onBuySelected={() =>
              setConfirm({
                title: `Buy ${selection.count} number${selection.count === 1 ? '' : 's'}?`,
                message: `Your card will be charged before the numbers are provisioned.`,
                confirmLabel: bulkPurchasing ? 'Buying…' : 'Confirm purchase',
                variant: 'primary',
                action: handleBulkPurchase,
              })
            }
          />
        )}
      </div>

      <AssignModal
        open={!!assigningDID}
        did={assigningDID}
        users={users}
        agents={agents}
        saving={savingAssignment}
        onCancel={() => setAssigningDID(null)}
        onAssign={handleAssign}
      />

      <RoutingModal
        open={!!routingDID}
        did={routingDID}
        form={routingForm}
        setForm={setRoutingForm}
        credentials={credentials}
        saving={savingRouting}
        onCancel={() => {
          setRoutingDID(null);
          setRoutingForm(null);
        }}
        onSave={saveRouting}
      />

      <ConfirmDialog
        open={!!confirm}
        onCancel={() => setConfirm(null)}
        onConfirm={() => confirm?.action?.()}
        title={confirm?.title}
        message={confirm?.message}
        confirmLabel={confirm?.confirmLabel}
        variant={confirm?.variant}
        loading={confirmBusy || bulkPurchasing}
      />
    </div>
  );
};

export default DIDManagement;
